import { useCallback, useMemo } from "react"
import { useLocation, useSearchParams } from "react-router"

import {
  stageSearchParamUpdates,
  useSearchParamStagingScope,
} from "@/utils/url/url-utils"
import { Interval } from "luxon"

interface UseSearchParamsDateRangeValue {
  isDateRangeInSearchParams: boolean
  searchParamsDateRange: Interval | undefined
  setSearchParamsDateRange: (
    dateRange: Interval,
    options?: SetSearchParamsDateRangeOptions
  ) => void
}

interface SetSearchParamsDateRangeOptions {
  replace?: boolean
}

/**
 * Manages the date range value in the search params.
 *
 * @example
 * const {
 *   isDateRangeInSearchParams,
 *   searchParamsDateRange,
 *   setSearchParamsDateRange
 * } = useSearchParamsDateRange()
 */
export const useSearchParamsDateRange = (): UseSearchParamsDateRangeValue => {
  const location = useLocation()
  const searchParamStagingScope = useSearchParamStagingScope()
  const [searchParams, setSearchParams] = useSearchParams()

  const searchParamsStart: string | null = searchParams.get("start")
  const searchParamsEnd: string | null = searchParams.get("end")

  const isDateRangeInSearchParams =
    Boolean(searchParamsStart) && Boolean(searchParamsEnd)

  const memoizedSearchParamsDateRange = useMemo(() => {
    return Interval.fromISO(`${searchParamsStart}/${searchParamsEnd}`)
  }, [searchParamsEnd, searchParamsStart])

  const searchParamsDateRange: Interval | undefined = isDateRangeInSearchParams
    ? memoizedSearchParamsDateRange
    : undefined

  const setSearchParamsDateRange = useCallback(
    (dateRange: Interval, options: SetSearchParamsDateRangeOptions = {}) => {
      const { replace = false } = options

      stageSearchParamUpdates({
        committed: searchParams,
        locationKey: location.key,
        replace,
        scope: searchParamStagingScope,
        setSearchParams,
        updates: {
          start: dateRange.start?.toISODate(),
          end: dateRange.end?.toISODate(),
        },
      })
    },
    [location.key, searchParamStagingScope, searchParams, setSearchParams]
  )

  return {
    isDateRangeInSearchParams,
    searchParamsDateRange,
    setSearchParamsDateRange,
  }
}
